import PropTypes from 'prop-types';
import { X, SlidersHorizontal, ListChecks, Type, Loader2 } from 'lucide-react';
import { Button } from '../ui/Button';

/**
 * Modal de prévisualisation d'un modèle de sondage
 * @component
 */
function TemplatePreviewModal({ template, isOpen, loading, onClose, onUse }) {
  if (!isOpen) return null;

  const questionsCount = template?.questions?.length || template?.questions_count || 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-card rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-200">
        {loading ? (
          /* État de chargement */
          <div className="flex items-center justify-center p-12">
            <div className="text-center space-y-4">
              <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
              <p className="text-muted-foreground">Chargement du modèle...</p>
            </div>
          </div>
        ) : template ? (
          <>
            {/* Header avec icône */}
            <div className="p-8 pb-6">
              <button
                onClick={onClose}
                className="absolute top-6 right-6 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none"
              >
                <X className="h-5 w-5" />
              </button>

              <div className="flex items-start gap-4">
                {/* Icône de satisfaction */}
                <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                  <svg className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01"/>
                  </svg>
                </div>

                <div className="flex-1 min-w-0">
                  <h2 className="text-2xl font-bold text-foreground mb-2">
                    {template.title}
                  </h2>
                  <p className="text-muted-foreground">
                    {template.description}
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-8 pb-8">
              {/* Section Questions */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  QUESTIONS DU MODÈLE ({questionsCount})
                </h3>

                {template.questions && template.questions.length > 0 ? (
                  <div className="space-y-4">
                    {template.questions.map((question, index) => (
                      <QuestionCard 
                        key={question.id || index} 
                        question={question} 
                        index={index} 
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Aucune question disponible</p>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-8 py-6 border-t bg-muted/20">
              <Button variant="outline" onClick={onClose} size="lg">
                Fermer
              </Button>
              <Button onClick={() => onUse(template)} size="lg" className="gap-2">
                Utiliser ce modèle
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Composant pour afficher une question dans le modal
 */
function QuestionCard({ question, index }) {
  const typeConfig = {
    SCALE: { label: "Échelle", icon: SlidersHorizontal },
    SINGLE_CHOICE: { label: "QCM", icon: ListChecks },
    MULTIPLE_CHOICE: { label: "QCM", icon: ListChecks },
    TEXT: { label: "Texte libre", icon: Type },
    RATING: { label: "Échelle", icon: SlidersHorizontal },
  };

  const questionType = question.question_type || question.type || 'TEXT';
  const config = typeConfig[questionType] || typeConfig.TEXT;
  const Icon = config.icon;

  // Récupérer les options (choices ou options)
  const options = question.choices || question.options || [];

  return (
    <div className="p-6 rounded-xl border bg-card hover:border-primary/20 transition-colors">
      {/* En-tête de la question */}
      <div className="flex items-start gap-4 mb-4">
        <div className="flex items-center gap-2 text-muted-foreground">
          <span className="text-lg font-semibold">Q{index + 1}</span>
          <Icon className="h-5 w-5" />
          <span className="text-sm font-medium">{config.label}</span>
        </div>
      </div>

      {/* Texte de la question */}
      <h4 className="text-base font-medium text-foreground mb-3">
        {question.text || question.question_text}
      </h4>

      {/* Options pour choix unique/multiple */}
      {(questionType === 'SINGLE_CHOICE' || questionType === 'MULTIPLE_CHOICE') && 
       options.length > 0 && (
        <ul className="space-y-2 ml-6">
          {options.map((option, idx) => (
            <li key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50" />
              {option.text || option.option_text || option}
            </li>
          ))}
        </ul>
      )}

      {/* Info obligatoire */}
      {question.is_required && (
        <p className="text-xs text-muted-foreground mt-3 italic">Question obligatoire</p>
      )}
    </div>
  );
}

QuestionCard.propTypes = {
  question: PropTypes.shape({
    id: PropTypes.number,
    text: PropTypes.string,
    question_text: PropTypes.string,
    question_type: PropTypes.string,
    type: PropTypes.string,
    is_required: PropTypes.bool,
    options: PropTypes.array,
    choices: PropTypes.array,
  }).isRequired,
  index: PropTypes.number.isRequired,
};

TemplatePreviewModal.propTypes = {
  template: PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
    description: PropTypes.string,
    questions: PropTypes.array,
    questions_count: PropTypes.number,
  }),
  isOpen: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onUse: PropTypes.func.isRequired,
};

export default TemplatePreviewModal;
